package ru.villawonka.app.common.mvvm

import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import kotlinx.coroutines.CoroutineExceptionHandler
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.CoroutineStart
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.MutableSharedFlow
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asSharedFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch
import ru.villawonka.app.getSentry
import kotlin.coroutines.CoroutineContext
import kotlin.coroutines.EmptyCoroutineContext

/**
 * Базовая вью модель, которая работает с [UIState]. UIState опредлеяется самостоятельно и не зависит
 * от каких-то внешних условий.
 *
 * @param S тип данных, с которым работает вью модель
 */
abstract class BaseViewModel<S : UIState> : ScreenModel {

    private val _uiState = MutableStateFlow(createInitialStateInternal())
    private val _events = MutableSharedFlow<SingleLiveEvent>()

    /**
     * Flow для текущего состояния UI. Подписываются на него только вью (Activity, Fragment, Dialog etc.)
     */
    val uiState: Flow<S> = _uiState.asStateFlow()

    /**
     * Flow для каких-либо событий, не имеющих отношения к [UIState].
     * Подписываются на него только вью (Activity, Fragment, Dialog etc.)
     */
    val events: Flow<SingleLiveEvent> = _events.asSharedFlow()

    protected fun launch(
        context: CoroutineContext = EmptyCoroutineContext + CoroutineExceptionHandler { _, throwable ->
            getSentry()?.captureException(throwable)
        },
        start: CoroutineStart = CoroutineStart.DEFAULT,
        block: suspend CoroutineScope.() -> Unit,
    ) = screenModelScope.launch(context, start, block)

    /**
     * Вызывается, когда требуется создать некоторое начальное состояние вью
     *
     * @return начальное состояние вью
     */
    protected abstract fun createInitialState(): S

    /**
     * Присвоение нового состояния вью
     */
    protected open suspend fun setState(uiState: S) {
        _uiState.emit(uiState)
    }

    /**
     * «Выстрел» одним событием, которое не связано с [UIState]
     */
    protected open suspend fun shootEvent(event: SingleLiveEvent) {
        _events.emit(event)
    }

    protected val currentStateValue: S
        get() = _uiState.value

    private fun createInitialStateInternal(): S = createInitialState()
}